/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

/* eslint-disable react-hooks/exhaustive-deps */

import { FC, PropsWithChildren, useEffect, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { IdleTimeoutModal } from './IdleTimeoutModal'
import moment from 'moment'
import { useAuth } from '../Auth2.0/useAuth'
import { useInterval } from '../hooks/useInterval'

const TIMEOUT_MINUTES = window.env.VITE_IDLE_TIMEOUT_MINUTES
const PROMPT_BEFORE_MINUTES = window.env.VITE_PROMPT_BEFORE_IDLE_MINUTES

export const IdleTimeoutProvider: FC<PropsWithChildren> = ({ children }) => {

  const [open, setOpen] = useState(false)

  const { logout } = useAuth()
  const [modalTimeLeft, setModalTimeLeft] = useState<number>(PROMPT_BEFORE_MINUTES * 60)

  const onIdle = () => {
    setOpen(false)
    logout()
  }

  const onActive = () => {
    setOpen(false)
  }

  const onPrompt = () => {
    setOpen(true)
  }

  const { getRemainingTime, activate } = useIdleTimer({
    crossTab: true,
    onIdle,
    onActive,
    onPrompt,
    timeout: TIMEOUT_MINUTES * 60 * 1_000,
    promptBeforeIdle: PROMPT_BEFORE_MINUTES * 60 * 1_000,
    syncTimers: 1000,
  })

  const formatLeft = (seconds: number, type: string) => {
    const now = moment()
    if (seconds > 60 && seconds % 60 == 0) {
      return `${type} in: ${Math.round(seconds / 60)} minutes (at ${now
        .add(seconds, 'seconds')
        .format('hh:mm:ss A')})`
    }
    if (seconds <= 60 && seconds > 0) {
      return `${type} in: ${seconds} seconds`
    }
  }

  const debugLog = (beforeIdle: number, beforePrompt: number) => {
    const idle = formatLeft(beforeIdle, 'Idle')
    const prompt = formatLeft(beforePrompt, 'Prompt')
    const values = [idle, prompt].filter(v => !!v)
    if (values.length == 0) return
  }

  useInterval(
    () => {
      const remainingTime = getRemainingTime()
      const left = Math.ceil(remainingTime / 1000)

    
      if (open) {
        setModalTimeLeft(left)
      }
    },
    open || true ? 1000 : null
  )

  const handleModalClose = () => {
    activate()
    setOpen(false)
    setModalTimeLeft(PROMPT_BEFORE_MINUTES * 60)
  }

  return (
    <>
      <>{children}</>
      <>
        <IdleTimeoutModal
          open={open == true}
          type={'prompt'}
          timeLeft={modalTimeLeft}
          onClose={handleModalClose}
          onContinueClick={handleModalClose}
        />
      </>
    </>
  )
}
